import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Star, Heart, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePatientStore } from '../stores/patient';
import { useConsultation } from '../hooks/useConsultation';
import { KoyoBranding } from '../components/KoyoBranding';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Textarea';

export function Feedback() {
  const navigate = useNavigate();
  const { consultation, patient, clear } = usePatientStore();
  const { submitFeedback } = useConsultation();

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to home if no consultation data
  useEffect(() => {
    if (!consultation && !patient) {
      navigate('/');
    }
  }, [consultation, patient, navigate]);

  // Auto-return to registration after submission
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        clear();
        navigate('/');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted, clear, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      setError('Please select a rating');
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitFeedback(rating, comment || undefined);
      setIsSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit feedback';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    clear();
    toast.info('Thank you for using Talk to a Doctor');
    navigate('/');
  };

  const getRatingMessage = (rating: number): string => {
    switch (rating) {
      case 1:
        return 'We apologize for the experience';
      case 2:
        return 'We will work to improve';
      case 3:
        return 'Thank you for your feedback';
      case 4:
        return 'Great! We appreciate your feedback';
      case 5:
        return 'Excellent! Thank you so much';
      default:
        return '';
    }
  };

  // Success state after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-2xl text-center flex-1 flex flex-col justify-center">
          {/* Success Animation */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="w-40 h-40 bg-emerald-600 rounded-full flex items-center justify-center animate-pulse">
                <CheckCircle className="size-24 text-white" />
              </div>
              {/* Radiating circles */}
              <div className="absolute inset-0 w-40 h-40 bg-emerald-500/30 rounded-full animate-ping" />
            </div>
          </div>

          <h1 className="text-5xl text-white mb-4">Thank You!</h1>
          <p className="text-2xl text-slate-400 mb-4">
            Your feedback has been received
          </p>
          <p className="text-xl text-slate-500">
            We hope you feel better soon
          </p>

          <div className="mt-12 bg-[#1e293b] rounded-3xl p-8 border border-slate-700">
            <Heart className="size-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-lg text-slate-300">
              Thank you for using Talk to a Doctor
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Returning to home screen...
            </p>
          </div>
        </div>

        {/* Koyo Branding */}
        <KoyoBranding className="py-6" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-2xl flex-1 flex flex-col justify-center">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="bg-emerald-600 p-3 rounded-2xl">
              <Heart className="size-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl text-white mb-2">How Was Your Visit?</h1>
          <p className="text-xl text-slate-400">Please rate your experience</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl">
            <p className="text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} className="bg-[#1e293b] rounded-3xl p-10 shadow-2xl border border-slate-700">
          {/* Star Rating */}
          <div className="mb-10">
            <p className="text-2xl text-slate-200 text-center mb-6">Rate your consultation</p>
            <div className="flex justify-center gap-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  disabled={isSubmitting}
                  className="transition-transform hover:scale-110 active:scale-95 touch-manipulation disabled:opacity-50"
                  style={{ minWidth: '60px', minHeight: '60px' }}
                >
                  <Star
                    className={`size-16 transition-all ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'fill-slate-700 text-slate-700'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center text-slate-400 mt-4 text-lg">
                {getRatingMessage(rating)}
              </p>
            )}
          </div>

          {/* Comment */}
          <div className="mb-8">
            <label htmlFor="comment" className="text-lg text-slate-200 mb-3 block">
              Additional Comments (Optional)
            </label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us more about your experience..."
              className="min-h-32 text-lg bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500 resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={rating === 0 || isSubmitting}
            className="w-full h-20 text-2xl bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-3">
                <Loader2 className="size-6 animate-spin" />
                Submitting...
              </span>
            ) : (
              'Submit Feedback'
            )}
          </Button>

          {rating === 0 && (
            <p className="text-center text-slate-500 mt-4 text-lg">
              Please select a rating to continue
            </p>
          )}

          {/* Skip Button */}
          <button
            type="button"
            onClick={handleSkip}
            disabled={isSubmitting}
            className="w-full mt-4 py-4 text-slate-400 hover:text-slate-300 transition-colors disabled:opacity-50"
          >
            Skip feedback
          </button>
        </form>
      </div>

      {/* Koyo Branding */}
      <KoyoBranding className="py-6" />
    </div>
  );
}
